import { z } from 'zod';

/** TI, Governança (backlog G1 a G4, G6 e G8; 0342): controles, exceções, RACI e incidentes. */

export const IT_CONTROL_FREQUENCIES = [
  'event',
  'monthly',
  'quarterly',
  'semiannual',
  'annual',
] as const;
export const IT_CONTROL_RESULTS = ['ok', 'issues', 'not_applicable'] as const;
export const IT_EXCEPTION_KINDS = ['exception', 'compensating', 'in_implementation'] as const;
export const IT_RACI_ROLES = ['R', 'A', 'C', 'I'] as const;
export const IT_INCIDENT_SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;
export const IT_INCIDENT_STATUSES = ['open', 'contained', 'resolved'] as const;
export const IT_SYSTEM_ENVIRONMENTS = ['production', 'development', 'both'] as const;

const id = z.uuid().nullable().optional();
const day = z.iso.date();
const text = (min: number, max: number) => z.string().trim().min(min).max(max);
const when = z.iso.datetime({ offset: true }).nullable().optional();

export const itControlInput = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[A-Z0-9][A-Z0-9_.-]{1,29}$/, 'Use de 2 a 30 letras maiúsculas, números, ., - ou _'),
  name: text(3, 200),
  description: text(1, 2000).optional(),
  frequency: z.enum(IT_CONTROL_FREQUENCIES),
  ownerUserId: id,
  isActive: z.boolean().default(true),
});
export type ItControlInput = z.infer<typeof itControlInput>;

export const itControlExecutionInput = z
  .object({
    legalEntityId: id,
    executedOn: day,
    result: z.enum(IT_CONTROL_RESULTS),
    note: text(3, 2000).optional(),
    evidenceUrl: z
      .string()
      .trim()
      .regex(/^(https:\/\/|\/)\S+$/, 'Use um endereço https:// ou interno')
      .max(500)
      .optional(),
  })
  .refine((v) => v.result === 'ok' || !!v.note, {
    path: ['note'],
    message: 'Explique o que foi encontrado',
  });

export const itControlExceptionInput = z
  .object({
    legalEntityId: z.uuid(),
    controlId: id,
    title: text(3, 200),
    kind: z.enum(IT_EXCEPTION_KINDS),
    justification: text(3, 2000),
    compensatingMeasure: text(3, 2000).optional(),
    ownerUserId: z.uuid(),
    dueOn: day,
  })
  .refine((v) => v.kind !== 'compensating' || !!v.compensatingMeasure, {
    path: ['compensatingMeasure'],
    message: 'Descreva o controle compensatório',
  });

export const itCloseNoteInput = z.object({ note: text(3, 2000) });

export const itRaciInput = z.object({
  process: text(2, 120),
  activity: text(2, 200),
  userId: z.uuid(),
  role: z.enum(IT_RACI_ROLES),
});

export const itIncidentInput = z.object({
  legalEntityId: z.uuid(),
  title: text(3, 200),
  description: text(3, 4000),
  severity: z.enum(IT_INCIDENT_SEVERITIES),
  detectedAt: z.iso.datetime({ offset: true }),
  personalData: z.boolean().default(false),
  ticketId: id,
});

/** Andamento do incidente: contenção, resolução e os marcos de comunicação (LGPD). */
export const itIncidentUpdateInput = z.object({
  status: z.enum(IT_INCIDENT_STATUSES),
  containedAt: when,
  resolvedAt: when,
  personalData: z.boolean(),
  dpoNotifiedAt: when,
  legalNotifiedAt: when,
  anpdRequired: z.boolean().nullable().optional(),
  anpdNotifiedAt: when,
  actions: text(3, 4000).optional(),
});

export const itEmergencyCredentialInput = z.object({
  systemId: z.uuid(),
  label: text(3, 200),
  holderUserId: z.uuid(),
  backupUserId: id,
  location: text(3, 300),
  lastCheckedOn: day.nullable().optional(),
  nextCheckOn: day.nullable().optional(),
  isActive: z.boolean().default(true),
});
